import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject, afterNextRender, ChangeDetectionStrategy, viewChild, signal, computed, afterRenderEffect, untracked } from '@angular/core';
import { ActivatedRoute, RouterLink, } from '@angular/router';
import { animate, query, sequence, state, style, transition, trigger } from '@angular/animations';
import { ResizeService } from '../../auth.service';
import { ProfileService } from './profile.service';
import { MetaService } from '../../meta.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { ProfileDetailsComponent } from './details/profile-details.component';
import { ProfilePreferencesComponent } from './preferences/preferences.component';
import { ProfileCouponsComponent } from './coupons/coupons.component';
import { ProfileSubscriptionsComponent } from './subscriptions/subscriptions.component';
import { lastValueFrom } from 'rxjs';
import { ProfileBillingComponent } from "./billing/billing.component";
import { ProfileRedemptionsComponent } from './redemptions/redemptions.component';
import { HumanizePipe } from '../../humanize.pipe';
import { ProfileAccountComponent } from './account/account.component';
import { ConfirmPopup } from '../../confirm.popup';

interface ProfileOption {
  id: string; 
  icon?: string; 
  _icon?: string; 
  title: string; 
  path: string; 
  artificial_route?: boolean; 
  hidden?: boolean;
}

@Component({
  selector: 'web-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  animations: [
    trigger('swap', [
      transition('* <=> *', [
          sequence([
              query(':enter', [
                  style({
                      opacity: 0,
                      transform: 'translateY(50vh)'
                  }),
              ], { optional: true }),
              query(':leave', [
                  style({
                      opacity: 1,
                      transform: 'translateY(0px)'
                  }),
                  animate('250ms linear', style({
                      opacity: 0,
                      transform: 'translateY(-5vh)'
                  })),
              ], { optional: true }),
              query(':enter', [
                  animate('900ms ease', style({
                      opacity: 1,
                      transform: 'translateY(0vh)'
                  }))
              ], { optional: true }),
          ]),
      ])
  ]),
    trigger('vanish', [
      state('1', style({
          opacity: 1
      })),
      state('0', style({
          opacity: 0
      })),
      transition('1 <=> 0', [
          animate("250ms cubic-bezier(0.455, 0.03, 0.515, 0.955)")
      ]),
  ])
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatButtonModule, RouterLink, MatTabsModule, HumanizePipe, DatePipe, NgTemplateOutlet, ProfileDetailsComponent, ProfilePreferencesComponent, ProfileCouponsComponent, ProfileSubscriptionsComponent, ProfileBillingComponent, ProfileRedemptionsComponent, ProfileAccountComponent, DecimalPipe, NgTemplateOutlet]
})
export default class ProfileComponent implements OnInit, OnDestroy {
  details = viewChild(ProfileDetailsComponent);
  preferences = viewChild(ProfilePreferencesComponent);
  confirm = inject(ConfirmPopup);
  size = inject(ResizeService);
  meta = inject(MetaService);
  service = inject(ProfileService);
  changes = inject(ChangeDetectorRef);
  activated = inject(ActivatedRoute);

  metaTagDesc = 'View and Manage your cloudigo details. Edit profile details, view and modify billing and also browse your redemption history';

  activeStat: number = 0;
  statTimer: any = null;

  displayName = signal<string[]>(['Cloudigo', 'User']);
  optionMenu: ProfileOption[] = [
    { id: 'details', _icon: 'person', title: 'My details', path: '/profile/details', artificial_route: true },
    { id: 'preferences', _icon: 'tune', title: 'My contact preferences', path: '/profile/preferences', artificial_route: true },
    { id: 'account', _icon: 'settings', title: 'My account', path: '/profile/account', artificial_route: true  },
    { id: 'referrals', _icon: 'group_add', title: 'Refer a friend', path: '/profile/referrals', artificial_route: true, hidden: true },
    { id: 'coupons', _icon: 'sell', title: 'My discount codes', path: '/profile/coupons', artificial_route: true },
    { id: 'membership', icon: 'premium', title: 'Subscriptions', path: '/profile/membership', artificial_route: true },
    { id: 'billing', _icon: 'credit_card', title: 'Billing', path: '/profile/billing', artificial_route: true, hidden: true },
    { id: 'redemptions', _icon: 'local_activity', title: 'My usage history', path: '/profile/redemptions', artificial_route: true },
    { id: 'get-app', _icon: 'phone_iphone', title: 'Get the app', path: '/get-the-app' },
    { id: 'terms', _icon: 'help_outline', title: 'Legal', path: '/terms-and-conditions' },
    { id: 'contact', _icon: 'help_outline', title: 'Need help?', path: '/contact-us' },
    { id: 'faqs', _icon: 'help_outline', title: 'FAQs', path: '/faqs' },
    { id: 'logout', _icon: 'logout', title: 'Log out', path: '/logout' },
  ];

  selectedIndex = signal<number>(-1);

  selectedOption = computed(() => {
      const index = this.selectedIndex();

      if(index == -1) return null;

      return this.optionMenu.at(index) || null;
  });

  constructor() {
    this.meta.setTags('cloudigo Malta - Profile', this.metaTagDesc);

    this.activated.paramMap.pipe(takeUntilDestroyed()).subscribe(paramChange => {
      let path = paramChange.get('page');
      // console.log(path);
      if (path && path != 'list') {
        // this.ss.location.replaceState('/profile');
        const initialOption = this.optionMenu.findIndex(o => o.id == path);
        this.selectedIndex.set(initialOption);//, true);
      } else {
        this.selectedIndex.set(this.size.width() > 760 ? 0 : -1);
      }

    });

    afterNextRender(() => {
      if(this.size.width() > 760 && this.selectedIndex() == -1){
        this.selectedIndex.set(0);
      }
    });

    afterRenderEffect(() => {
      const user = this.service.profile();

      untracked(() => {
        if(user){
  
          this.displayName.set([user.name, user.surname]);
  
          const hasMembershipOrCard = this.service.membership()?.transaction || !!this.service?.membership()?.cards || !!this.service?.membership()?.payment_methods;
  
          const billingOption = this.optionMenu.find(o => o.id == 'billing');
          if(billingOption){
            billingOption.hidden = !hasMembershipOrCard;
          }
  
          try {
            localStorage?.setItem('user', user.name);
          } catch (e) {}
          this.meta.displayName.set(user.name);
        }
      });
    });


    this.size.resizer.subscribe(bodyRect => {
      if(bodyRect.width > 760 && this.selectedIndex() == -1){
        this.service.router.navigate([this.optionMenu[0].path]);
      }
    });
  
    // this.statTimer = setInterval(() => {
    //   this.activeStat++;
    //   if (this.activeStat >= 2)
    //     this.activeStat = 0;
    // }, 4000);
  }

  ngOnInit() {
      
  }

  ngOnDestroy(): void {
    clearInterval(this.statTimer);
  }

  renew(){
    //TODO: make this button work
    // this.ss.trackPremiumClick('Profile Panel', 'Resubscribe');
    // this.ss.addCreditCard(this.pay, this.service.subscription, null, true);
  }

  async preNavigate(option: ProfileOption|null){

    if(this.selectedOption()?.id == option?.id) return;

    const selectedComponent = this.details() || this.preferences() || null;
    if(selectedComponent?.countSame()) {
      const state = await lastValueFrom(this.confirm.open('Unsaved changes', 'You currently have changes that were not saved yet. Would you like to proceed anyways?', 'Pending Changes Prompt', [
        {
          label: 'Stay',
          paint: 'primary',
          close: true
        },
        {
          label: 'Save and Proceed',
          paint: 'warn',
          close: true
        },
        {
          label: 'Proceed',
          paint: 'warn',
          close: true
        },
      ], 'Stay').afterClosed());

      switch(state){
        case 'Save and Proceed'://SAVE AND GO
          selectedComponent.submit(() => this.navigate(option));
        break;
        case 'Proceed'://GO
        break;
        default://STOP
          selectedComponent.form.markAllAsTouched();
        return;
      }
    }

    this.navigate(option);

  }
  
  navigate(option: ProfileOption|null){
    this.service.loading.set(false);

    if(!option){
      this.service.router.navigate(['/profile/list']);
    } else {
      this.service.router.navigate([option.path]);  
    }
  }
}
